// M005-F0053 (M005-D005 §6, WEB-BKHUB-003/004/005) — Studio tab.
//
// Salvage, not rebuild (§6.0): this is the dead/unrouted AdminBookings
// component (src/components/admin/AdminComponents.js, WebUI issue #64 —
// this file is the design's "route it" answer) rehomed into the hub with
// the org selector removed (the hub shell already scopes the selected
// organisation) and the four things §6.0 ordered fixed on the way in:
//   1. the grouping is the richer series grouping ported into
//      bookings-admin/ (studioBookingsView.js -> bookingsGrouping.js,
//      the expandOccurrences path) — one card per series with from/until
//      and the per-date exception overlay, never a dedupe-and-count row;
//   2. room/person/date-range filters are added, wired to the real
//      GET /api/bookings query parameters (room/user/start/end);
//   3. the default view is EVERYTHING in the organisation with pending
//      floated first (the old component filtered the list DOWN to
//      status=pending by default);
//   4. Approve/Decline render only for an actor whose selected-org
//      my-permissions projection grants `bookings_manage_all` (the routes
//      already enforce it server-side — this is the UI-visibility layer,
//      never a role-name check).
//
// Approve is a single click and Decline requires a reason (reject route
// takes `reason`); both refetch from the real server response — a card's
// status is never updated optimistically (WEB-BKHUB-004/005). Live refresh
// on the booking:* WebSocket events is kept from the salvaged component.
//
// M004-F0678 (WebUI #108/#81, M004-F0637 job 1+2): a card is the click target
// for the shared BookingDrawer — the ONE per-booking surface: requester,
// times, Approve / Decline (the decline reason is INLINE in the drawer, no
// modal: UI-4) and the Price section. The card's own Approve / Decline buttons
// and the drawer's call the SAME handlers (one approve, one decline). The old
// "Edit booking" link is gone (it led to the caller's own-bookings page).
import React, { useEffect, useRef, useState } from 'react';
import {
  bookingApi,
  organisationApi,
  roomApi,
} from '../../services/api';
import wsService from '../../services/websocket';
import BookingDrawer from './BookingDrawer';
import {
  buildSeriesKey,
  formatNextHeadline,
  formatOccurrenceRow,
  formatSeriesHeadline,
  formatSingleBookingWhen,
} from './bookingsGrouping';
import { getStatusBadgeClass, getStatusText, resolveDisplayStatus } from './bookingStatus';
import {
  buildStudioCards,
  isPendingDisplayStatus,
  requesterNameOf,
  studioBookingsMeta,
  studioBookingsQuery,
  STUDIO_STATUS_FILTERS,
} from './studioBookingsView';
import './BookingsHub.css';

const responseList = (response) => response?.data?.data ?? response?.data ?? [];

const personIdOf = (person) => String(person?.user_id ?? person?.id ?? '');
const personLabelOf = (person) => person?.display_name || person?.name || `User ${personIdOf(person)}`;

// The booking rows the grouping keeps as a series card's per-date list each
// carry their own status (a cancelled/pending exception must surface
// per-date, not be hidden behind the series' overall state) — so an actor
// who can manage bookings gets the action on THAT date, not on the series.
const pendingOccurrenceIds = (card) => (card.occurrences || [])
  .filter((occurrence) => occurrence.isException && isPendingDisplayStatus(occurrence.statusKey))
  .map((occurrence) => occurrence.sourceBooking?.id)
  .filter((id) => id !== undefined && id !== null);

const StudioCard = ({
  card,
  canManage,
  processingId,
  onApprove,
  onOpenReject,
  onOpenDrawer,
}) => (
  <article
    className="bkh-card bkh-studio-card"
    data-testid="bkh-studio-card"
    data-kind={card.kind}
    data-booking-id={card.id}
    // M004-F0678: the card is the click target; the drawer opens for the
    // card's representative row.
    onClick={onOpenDrawer}
  >
    <div className="bkh-card-main">
      <span className={card.statusBadgeClass} data-testid="bkh-studio-card-status">{card.statusText}</span>
      <p className="bkh-card-title">{card.title}</p>
      <p className="bkh-card-person" data-testid="bkh-studio-card-person">{card.personName}</p>
      <p className="bkh-card-when">
        {card.kind === 'series'
          ? formatSeriesHeadline(card)
          : formatSingleBookingWhen(card.start, card.end)}
      </p>
      <p className="bkh-card-next">{formatNextHeadline(card) || ''}</p>
      <p className="bkh-card-room">{card.roomName}</p>
      {card.id != null ? (
        <p className="bkh-card-edit">
          <button
            type="button"
            className="btn btn-outline btn-sm bkh-card-open"
            data-testid="bkh-studio-open"
            onClick={(event) => { event.stopPropagation(); onOpenDrawer(); }}
          >
            Open booking
          </button>
        </p>
      ) : null}
      {card.statusKey === 'rejected' && card.mainBooking.rejection_reason ? (
        <p className="bkh-studio-rejection-reason" data-testid="bkh-studio-rejection-reason">
          Reason: {card.mainBooking.rejection_reason}
        </p>
      ) : null}
    </div>
    {card.kind === 'series' && card.occurrences.length > 0 ? (
      <details className="bkh-card-dates">
        <summary>
          {`${card.occurrences.length} date${card.occurrences.length === 1 ? '' : 's'}`}
          {card.isTruncated ? ' (list truncated)' : ''}
        </summary>
        <ul>
          {card.occurrences.map((occurrence, index) => (
            <li key={`${occurrence.start.getTime()}-${index}`}>
              {formatOccurrenceRow(occurrence)}
            </li>
          ))}
        </ul>
      </details>
    ) : null}
    {canManage ? (
      <div
        className="bkh-studio-card-actions"
        data-testid="bkh-studio-card-actions"
        onClick={(event) => event.stopPropagation()}
      >
        {card.isPending ? (
          <>
            <button
              type="button"
              className="btn btn-sm btn-success"
              data-testid="bkh-studio-approve"
              disabled={processingId === card.id}
              onClick={() => onApprove(card.id)}
            >
              {processingId === card.id ? '…' : '✓ Approve'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              data-testid="bkh-studio-reject"
              disabled={processingId === card.id}
              onClick={() => onOpenReject(card.mainBooking)}
            >
              ✗ Decline
            </button>
          </>
        ) : pendingOccurrenceIds(card).map((pendingId) => (
          <span key={pendingId} className="bkh-studio-occurrence-actions">
            <button
              type="button"
              className="btn btn-sm btn-success"
              data-testid="bkh-studio-approve"
              disabled={processingId === pendingId}
              onClick={() => onApprove(pendingId)}
            >
              {processingId === pendingId ? '…' : '✓ Approve date'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              data-testid="bkh-studio-reject"
              disabled={processingId === pendingId}
              onClick={() => onOpenReject(card.occurrences.find(
                (occurrence) => occurrence.sourceBooking?.id === pendingId,
              )?.sourceBooking)}
            >
              ✗ Decline date
            </button>
          </span>
        ))}
      </div>
    ) : null}
  </article>
);

const StudioTab = ({
  organisationId, organisationName, permissions, focusBookingId = null,
}) => {
  const [bookings, setBookings] = useState([]);
  const [totalRows, setTotalRows] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [people, setPeople] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  // M004-F0678: the open drawer holds a booking row id; the row itself is
  // derived from `bookings` (server truth after every refetch), with a singly
  // fetched row as the fallback for a booking the loaded page/filter does not
  // contain (a ?booking= deep link, or a booking that just left the filter by
  // being approved).
  const [drawerBookingId, setDrawerBookingId] = useState(null);
  const [drawerRowFallback, setDrawerRowFallback] = useState(null);
  const [focusError, setFocusError] = useState('');
  const focusHandledRef = useRef(null);
  // The decline reason lives in the drawer (UI-4): no modal.
  const [declineOpen, setDeclineOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const canManage = permissions?.bookings_manage_all === true;

  const filters = {
    orgId: organisationId,
    status: statusFilter,
    roomId: roomFilter,
    userId: personFilter,
    startDate: startDateFilter,
    endDate: endDateFilter,
  };
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // The live-refresh subscription (below) is mounted once, so its handler
  // must read the CURRENT accumulated rows through a ref, the same way it
  // reads filters through filtersRef.
  const bookingsRef = useRef(bookings);
  bookingsRef.current = bookings;

  // append=false (filters/org/refresh/live events) reloads page one and
  // replaces the list; append=true (Load more) pages with the route's
  // `offset` parameter and extends it — the default 'everything' view is
  // capped at STUDIO_BOOKINGS_LIMIT per response, and the rest of the org
  // must stay reachable instead of silently truncated (!351 finding 1).
  const fetchBookings = async ({ append = false } = {}) => {
    const current = filtersRef.current;
    if (!current.orgId) return;
    if (append) setLoadingMore(true); else setLoading(true);
    setError('');
    try {
      const offset = append ? bookingsRef.current.length : 0;
      const response = await bookingApi.getAllBookings(studioBookingsQuery({ ...current, offset }));
      const rows = responseList(response);
      setBookings((previous) => (append ? [...previous, ...rows] : rows));
      setTotalRows(studioBookingsMeta(response)?.total ?? null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Filters (and org switches) refetch with the real query parameters —
  // never client-only filtering of an unbounded fetch (WEB-BKHUB-003).
  // fetchBookings reads the filters through filtersRef, so listing the
  // filter states (not the function) as the deps is the intended trigger.
  useEffect(() => {
    fetchBookings();
  }, [organisationId, statusFilter, roomFilter, personFilter, startDateFilter, endDateFilter]);

  // Filter drop-down options for the selected organisation. A failed read
  // leaves the option list empty rather than guessing; the filters stay
  // wired to their query parameters either way.
  useEffect(() => {
    let current = true;
    setRooms([]);
    setPeople([]);
    setRoomFilter('');
    setPersonFilter('');
    if (!organisationId) return () => { current = false; };
    roomApi.getRooms(organisationId)
      .then((response) => { if (current) setRooms(responseList(response)); })
      .catch(() => {});
    organisationApi.getUsers(organisationId)
      .then((response) => { if (current) setPeople(responseList(response)); })
      .catch(() => {});
    return () => { current = false; };
  }, [organisationId]);

  // Live refresh on the booking:* WebSocket events — salvaged verbatim in
  // spirit from AdminBookings (§6.0); one subscription whose handler always
  // refetches with the CURRENT filters via filtersRef.
  useEffect(() => {
    wsService.connect();
    const events = ['booking:created', 'booking:updated', 'booking:approved', 'booking:rejected', 'booking:deleted'];
    const unsubscribers = events.map((event) => wsService.subscribe(event, () => {
      fetchBookings();
    }));
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    // One subscription for the tab's lifetime: the handler refetches with
    // the CURRENT filters via filtersRef, so it needs no reactive deps.
  }, []);

  // Re-reads ONE booking row: after an action the list refetch may no longer
  // contain it (an approved booking leaves the "Pending" filter), and the
  // drawer must keep showing server truth for it rather than vanish.
  const loadDrawerRow = async (bookingId) => {
    try {
      const response = await bookingApi.getBooking(bookingId);
      const row = responseList(response);
      if (row && row.id != null) setDrawerRowFallback(row);
    } catch (err) {
      // The list refetch is the source of truth; a failed single read leaves
      // the drawer on whatever the list holds.
    }
  };

  const refreshAfterAction = async (bookingId = null) => {
    // Server-confirmed only: the list is refetched and the card's status
    // comes from the real server response, never optimistically.
    await fetchBookings();
    if (bookingId != null) await loadDrawerRow(bookingId);
  };

  const handleApprove = async (bookingId) => {
    setProcessingId(bookingId);
    setActionError('');
    try {
      await bookingApi.approveBooking(bookingId);
      await refreshAfterAction(String(drawerBookingId) === String(bookingId) ? bookingId : null);
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to approve booking');
    } finally {
      setProcessingId(null);
    }
  };

  // Decline opens THE DRAWER with the reason box (one decline implementation,
  // the same for the card button and the drawer button).
  const openDecline = (booking) => {
    if (!booking || booking.id == null) return;
    setDrawerBookingId(booking.id);
    setRejectReason('');
    setDeclineOpen(true);
  };

  const closeDrawer = () => {
    setDrawerBookingId(null);
    setDrawerRowFallback(null);
    setDeclineOpen(false);
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectReason.trim() || drawerBookingId == null) return;
    const bookingId = drawerBookingId;
    setProcessingId(bookingId);
    setActionError('');
    try {
      await bookingApi.rejectBooking(bookingId, rejectReason.trim());
      setDeclineOpen(false);
      setRejectReason('');
      await refreshAfterAction(bookingId);
    } catch (err) {
      setActionError(err?.response?.data?.message || 'Failed to reject booking');
    } finally {
      setProcessingId(null);
    }
  };

  const cards = buildStudioCards(bookings);
  const pendingCount = bookings.filter((booking) => (
    isPendingDisplayStatus(resolveDisplayStatus(booking).key)
  )).length;

  // The drawer's booking: the CURRENT list row (server truth after every
  // refetch) or, when the list no longer holds it, the singly fetched row.
  const drawerBooking = bookings.find(
    (booking) => String(booking.id) === String(drawerBookingId),
  ) || (drawerRowFallback && String(drawerRowFallback.id) === String(drawerBookingId)
    ? drawerRowFallback
    : null);
  const drawerStatusKey = drawerBooking ? resolveDisplayStatus(drawerBooking).key : null;
  const drawerPending = isPendingDisplayStatus(drawerStatusKey);
  const drawerSeriesKey = drawerBooking ? buildSeriesKey(drawerBooking) : null;
  const drawerIsSeries = Boolean(drawerSeriesKey);
  const drawerOccurrence = drawerBooking
    ? {
      title: drawerBooking.title || `Booking ${drawerBooking.id}`,
      roomName: drawerBooking.room_name || `Room ${drawerBooking.room_id ?? ''}`.trim(),
      start: new Date(drawerBooking.start_time),
      end: new Date(drawerBooking.end_time),
      statusKey: drawerStatusKey,
      statusText: getStatusText(drawerBooking),
      statusBadgeClass: getStatusBadgeClass(drawerBooking),
      sourceBooking: drawerBooking,
      bookingId: drawerBooking.id,
      seriesKey: drawerSeriesKey,
      seriesBookingId: drawerIsSeries ? drawerBooking.id : null,
    }
    : null;

  // ?booking=<id> (the hub's deep link, M004-F0678): open THAT booking's
  // drawer once the list has loaded. A booking the loaded page/filter does not
  // contain is FETCHED SINGLY and its drawer still opens; one that cannot be
  // fetched says WHY in a sentence — never a silently closed drawer.
  useEffect(() => {
    if (!focusBookingId || loading) return undefined;
    if (focusHandledRef.current === focusBookingId) return undefined;
    focusHandledRef.current = focusBookingId;
    setFocusError('');
    const inList = bookings.find((booking) => String(booking.id) === String(focusBookingId));
    if (inList) {
      setDrawerBookingId(inList.id);
      return undefined;
    }
    let current = true;
    bookingApi.getBooking(focusBookingId)
      .then((response) => {
        if (!current) return;
        const single = responseList(response);
        if (single && single.id != null) {
          setDrawerRowFallback(single);
          setDrawerBookingId(single.id);
        } else {
          setFocusError(`Booking ${focusBookingId} could not be opened: the server returned no such booking.`);
        }
      })
      .catch((err) => {
        if (!current) return;
        const reason = err?.response?.data?.message || err?.message;
        setFocusError(
          `Booking ${focusBookingId} could not be opened: `
          + (reason || 'it may not exist in this organisation, or you may not have access to it.'),
        );
      });
    return () => { current = false; };
  }, [bookings, focusBookingId, loading]);

  return (
    <div className="bkh-studio" data-testid="bookings-hub-tabpanel-studio">
      <h2>Studio — all bookings in {organisationName || 'this organisation'}</h2>
      <p className="bkh-studio-subtitle">
        {pendingCount > 0
          ? `${pendingCount} booking${pendingCount > 1 ? 's' : ''} waiting for approval`
          : 'Nothing waiting for approval'}
        {canManage ? '' : ' — view only (approve/decline needs bookings_manage_all)'}
      </p>

      <div className="bkh-studio-filters" data-testid="bkh-studio-filters">
        <label>
          Status
          <select
            data-testid="bkh-studio-filter-status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {STUDIO_STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          Room
          <select
            data-testid="bkh-studio-filter-room"
            value={roomFilter}
            onChange={(event) => setRoomFilter(event.target.value)}
          >
            <option value="">All rooms</option>
            {rooms.map((room) => (
              <option key={room.id} value={String(room.id)}>{room.name}</option>
            ))}
          </select>
        </label>
        <label>
          Person
          <select
            data-testid="bkh-studio-filter-person"
            value={personFilter}
            onChange={(event) => setPersonFilter(event.target.value)}
          >
            <option value="">Everyone</option>
            {people.map((person) => (
              <option key={personIdOf(person)} value={personIdOf(person)}>{personLabelOf(person)}</option>
            ))}
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            data-testid="bkh-studio-filter-from"
            value={startDateFilter}
            onChange={(event) => setStartDateFilter(event.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            data-testid="bkh-studio-filter-to"
            value={endDateFilter}
            onChange={(event) => setEndDateFilter(event.target.value)}
          />
        </label>
        <button type="button" className="btn btn-outline btn-sm" data-testid="bkh-studio-refresh" onClick={() => fetchBookings()}>
          🔄 Refresh
        </button>
      </div>

      {error ? <p className="bkh-error" role="alert" data-testid="bkh-studio-error">{error}</p> : null}
      {actionError ? <p className="bkh-error" role="alert" data-testid="bkh-studio-action-error">{actionError}</p> : null}
      {focusError ? <p className="bkh-error" role="alert" data-testid="bkh-studio-focus-error">{focusError}</p> : null}

      {/* The route caps every response at STUDIO_BOOKINGS_LIMIT rows; when the
          org genuinely holds more, say so and keep the rest reachable — never
          a silently truncated list (!351 finding 1). */}
      {totalRows !== null && bookings.length < totalRows ? (
        <div className="bkh-studio-paging">
          <p className="bkh-studio-count" data-testid="bkh-studio-count">
            Showing {bookings.length} of {totalRows} bookings — not all matching bookings are loaded yet.
          </p>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            data-testid="bkh-studio-load-more"
            disabled={loadingMore}
            onClick={() => fetchBookings({ append: true })}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}


      {loading && bookings.length === 0 ? (
        <p className="bkh-loading" data-testid="bkh-studio-loading">Loading bookings…</p>
      ) : cards.length === 0 ? (
        <p className="bkh-empty" data-testid="bkh-studio-empty">No bookings match these filters.</p>
      ) : (
        <div className="bkh-studio-cards" data-testid="bkh-studio-cards">
          {cards.map((card) => (
            <StudioCard
              key={card.key}
              card={card}
              canManage={canManage}
              processingId={processingId}
              onApprove={handleApprove}
              onOpenReject={openDecline}
              onOpenDrawer={() => setDrawerBookingId(card.id)}
            />
          ))}
        </div>
      )}

      {drawerOccurrence ? (
        <BookingDrawer
          occurrence={drawerOccurrence}
          onClose={closeDrawer}
          permissions={permissions}
          organisationId={organisationId}
          onBookingSaved={() => refreshAfterAction(drawerBookingId)}
          actionsSlot={canManage && drawerPending ? (
            <div className="bkh-studio-drawer-actions">
              {declineOpen ? (
                <div className="bkh-studio-decline" data-testid="bkh-studio-decline-form">
                  <p>
                    Declining <strong>{drawerBooking.title}</strong>, requested by{' '}
                    <span data-testid="bkh-studio-decline-requester">{requesterNameOf(drawerBooking)}</span>.
                  </p>
                  <label htmlFor="bkh-studio-reject-reason">Reason for declining *</label>
                  <textarea
                    id="bkh-studio-reject-reason"
                    className="form-input"
                    rows="3"
                    data-testid="bkh-studio-reject-reason"
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                    placeholder="The member sees this reason on their booking."
                    required
                  />
                  <div className="bkh-studio-decline-buttons">
                    <button type="button" className="btn btn-outline btn-sm" data-testid="bkh-studio-reject-cancel" onClick={() => setDeclineOpen(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      data-testid="bkh-studio-reject-confirm"
                      onClick={handleReject}
                      disabled={!rejectReason.trim() || Boolean(processingId)}
                    >
                      {processingId ? 'Declining…' : 'Decline booking'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    data-testid="bkh-drawer-approve"
                    disabled={processingId === drawerBooking.id}
                    onClick={() => handleApprove(drawerBooking.id)}
                  >
                    {processingId === drawerBooking.id ? '…' : '✓ Approve'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    data-testid="bkh-drawer-decline"
                    onClick={() => openDecline(drawerBooking)}
                  >
                    ✗ Decline
                  </button>
                </>
              )}
            </div>
          ) : null}
        />
      ) : null}
    </div>
  );
};

export default StudioTab;
